import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { EventType, InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest, mfaStepUpAuthority } from "./msalConfig";
import { getDeviceFingerprint } from "../utils/deviceFingerprint";

const AuthContext = createContext(null);

/** B2C custom policies are conventionally named like ".../B2C_1A_MFA" --
 * the last path segment is the policy name that shows up in the ID token's
 * "tfp" (or "acr") claim after a successful run of that policy. */
function mfaPolicyName() {
  return mfaStepUpAuthority?.split("/").filter(Boolean).pop();
}

/* WHEN NO STEP-UP AUTHORITY IS CONFIGURED -- every environment on Entra
 * External ID, dev included -- there is no B2C policy to redirect to, and
 * loginRedirect with a blank authority silently does nothing: "Verify
 * Identity" closed its dialog and the admin was left unable to approve
 * anything. What this tenant CAN do is force a fresh sign-in
 * (prompt=login), which re-checks the password and whatever MFA the tenant
 * requires. The redirect throws the page state away, so the attempt is
 * remembered in sessionStorage and credited only when the ID token that
 * comes back was issued after it. */
const STEP_UP_KEY = "bk_admin_stepup_started";
const STEP_UP_MAX_AGE_MS = 10 * 60_000;

export function startedFreshSignIn(claims, startedAtMs, nowMs = Date.now()) {
  if (!startedAtMs || nowMs - startedAtMs > STEP_UP_MAX_AGE_MS) return false;
  // 5s of slack for clock skew between the browser and the token service.
  return Number(claims?.iat) * 1000 >= startedAtMs - 5000;
}

/* The elevation itself is also kept for its 5-minute life. A completed
 * step-up reloads the page, and anything held only in React state is gone by
 * the time the admin can click Approve again -- which looked like the
 * verification having done nothing, and sent them round the loop. */
const REAUTH_KEY = "bk_admin_reauth_at";
const ELEVATION_MS = 5 * 60_000;

function readReauthAt() {
  try {
    const at = Number(sessionStorage.getItem(REAUTH_KEY)) || null;
    return at && Date.now() - at < ELEVATION_MS ? at : null;
  } catch { return null; }
}
function writeReauthAt(at) {
  try { sessionStorage.setItem(REAUTH_KEY, String(at)); } catch { /* storage blocked */ }
}

/** Called when a redirect sign-in has just COMPLETED. Credits the
 * re-verification if this tab started a step-up in the last ten minutes.
 * Returns whether it did. Plain storage, no React: main.jsx calls it before
 * anything renders, which is the one moment that does not depend on effect
 * ordering or on the account's claims having caught up. */
export function creditStepUpIfStarted() {
  const started = readStepUpStart();
  if (!started) return false;
  clearStepUpStart();
  const fresh = Date.now() - started <= STEP_UP_MAX_AGE_MS;
  if (fresh) writeReauthAt(Date.now());
  return fresh;
}

function readStepUpStart() {
  try { return Number(sessionStorage.getItem(STEP_UP_KEY)) || null; } catch { return null; }
}
function clearStepUpStart() {
  try { sessionStorage.removeItem(STEP_UP_KEY); } catch { /* storage blocked */ }
}

/**
 * Same B2C/MSAL shape as web's AuthContext, plus `forbidden`: the signed-in
 * B2C account may be valid but not a member of the admin group (checked
 * server-side, see api/services/auth.py's _is_admin) -- every 403 from the
 * API flips this so the app can show "you don't have admin access" instead
 * of silently failing per-request.
 *
 * `jitRequired` is the sibling flag for the narrower case: the account IS an
 * admin, but this route additionally requires an active, second-admin-
 * approved app.JITAccessRequests grant (secure_endpoint's require_jit) and
 * the caller doesn't have one. apiClient's response interceptor flips this
 * on the distinct `jit_access_required` error_code (as opposed to the
 * generic `forbidden`), and AuthedApp (App.jsx) renders AccessRequestPage
 * instead of the normal routes while it's set -- same gating shape as
 * `forbidden`/ForbiddenPage, just a different destination.
 */
export function AuthProvider({ children }) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [forbidden, setForbidden] = useState(false);
  const [jitRequired, setJitRequired] = useState(false);
  const [lastReauthAt, setLastReauthAtState] = useState(readReauthAt);
  const setLastReauthAt = useCallback((at) => { writeReauthAt(at); setLastReauthAtState(at); }, []);

  const account = accounts[0] || null;

  // Detects "the account just came back from a step-up MFA redirect" by
  // checking which B2C policy actually issued this ID token, rather than
  // just trusting the redirect succeeded -- a plain silent token refresh
  // shouldn't count as a fresh re-auth for useRequireReauth's elevation
  // window.
  useEffect(() => {
    const claims = account?.idTokenClaims;
    const policy = claims?.tfp || claims?.acr;
    if (policy && policy === mfaPolicyName()) {
      setLastReauthAt(Date.now());
      return;
    }
    const started = readStepUpStart();
    if (started) {
      if (startedFreshSignIn(claims, started)) setLastReauthAt(Date.now());
      // Credited, expired or stale: either way the attempt is over.
      if (Date.now() - started > STEP_UP_MAX_AGE_MS || startedFreshSignIn(claims, started)) clearStepUpStart();
    }
  }, [account]);

  // The signal that a forced sign-in actually COMPLETED. The effect above
  // reads the account's claims, which can still be the previous token's at
  // the moment it first runs after the redirect; this fires when MSAL has
  // finished processing the response, so it does not depend on that timing.
  useEffect(() => {
    const id = instance.addEventCallback((event) => {
      if (event.eventType !== EventType.LOGIN_SUCCESS) return;
      if (creditStepUpIfStarted()) setLastReauthAt(Date.now());
    });
    return () => { if (id) instance.removeEventCallback(id); };
  }, [instance, setLastReauthAt]);

  const login = useCallback(() => instance.loginRedirect(loginRequest), [instance]);
  const logout = useCallback(() => instance.logoutRedirect(), [instance]);

  /** Redirects to the MFA step-up B2C policy. Called both by apiClient's
   * 401 mfa_required handler and directly by hooks/useRequireReauth.js
   * before a sensitive admin action. B2C's step-up is a full-page redirect,
   * so there is no seamless "resume the action after re-auth" here -- the
   * admin re-clicks the action once they're back. */
  const stepUpMfa = useCallback(() => {
    if (mfaStepUpAuthority) {
      return instance.loginRedirect({ ...loginRequest, authority: mfaStepUpAuthority });
    }
    try { sessionStorage.setItem(STEP_UP_KEY, String(Date.now())); } catch { /* storage blocked */ }
    return instance.loginRedirect({ ...loginRequest, prompt: "login" });
  }, [instance]);

  const markReauthenticated = useCallback(() => setLastReauthAt(Date.now()), [setLastReauthAt]);

  const getAccessToken = useCallback(async () => {
    if (!account) throw new Error("Not authenticated");
    try {
      const result = await instance.acquireTokenSilent({ ...loginRequest, account });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect(loginRequest);
        return null;
      }
      throw err;
    }
  }, [instance, account]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      account,
      login,
      logout,
      stepUpMfa,
      getAccessToken,
      deviceFingerprint: getDeviceFingerprint(),
      forbidden,
      setForbidden,
      jitRequired,
      setJitRequired,
      lastReauthAt,
      markReauthenticated,
    }),
    [isAuthenticated, account, login, logout, stepUpMfa, getAccessToken, forbidden, jitRequired, lastReauthAt, markReauthenticated]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
